/**
 * Page de suivi scolarité : tableau récapitulatif des dépôts de relevés de
 * devoirs et sujets d'examen, par matière et formateur.
 *
 * Objectifs :
 *   - Repérer d'un coup d'œil les relevés manquants (bandeau rouge + KPI)
 *   - Traçabilité : qui a saisi (formateur / scolarité), quand
 *   - Filtrer par niveau, semestre, source
 *   - Onglet prévu pour les sujets d'examen (Phase 2)
 */
import React, { useState } from 'react';
import {
  ClipboardCheck, FileText, AlertTriangle, CheckCircle2,
  Loader, Users, User, RefreshCw, Bell, Search, X, Send, Printer,
} from 'lucide-react';
import {
  useGetSuiviRelevesQuery,
  useGetSuiviSujetsQuery,
  useEnvoyerRappelFormateursMutation,
  type SuiviReleveLigne,
  type SuiviSujetLigne,
} from '../../services/suiviScolariteApi';
import { useGetPolesQuery } from '../../services/polesOptionsApi';
import { useGetInfoInstitutQuery } from '../../services/coreApi';

type Onglet = 'releves' | 'sujets_normale' | 'sujets_rattrapage';
type StatutOnglet = 'manquant' | 'partiel' | 'depose';

const NIVEAUX = ['', 'L1', 'L2', 'L3', 'M1', 'M2'];

const SuiviScolarite: React.FC = () => {
  const [onglet, setOnglet] = useState<Onglet>('releves');
  const [statutOnglet, setStatutOnglet] = useState<StatutOnglet>('manquant');
  const [semestre, setSemestre] = useState<1 | 2>(1);
  const [niveau, setNiveau] = useState<string>('');
  const [pole, setPole] = useState<string>('');
  const [recherche, setRecherche] = useState('');

  const { data: poles = [] } = useGetPolesQuery({ actif: true });
  const { data: institutInfo } = useGetInfoInstitutQuery();
  const { data: dataReleves, isLoading: loadingReleves, isFetching: fetchingReleves, refetch: refetchReleves } = useGetSuiviRelevesQuery(
    { semestre, niveau: niveau || undefined, pole: pole || undefined, source: 'all' },
    { skip: onglet !== 'releves' },
  );
  const type_session = onglet === 'sujets_normale' ? 'normale' : 'rattrapage';
  const { data: dataSujets, isLoading: loadingSujets, isFetching: fetchingSujets, refetch: refetchSujets } = useGetSuiviSujetsQuery(
    { semestre, type_session, niveau: niveau || undefined, pole: pole || undefined },
    { skip: onglet === 'releves' },
  );
  const [envoyerRappel, { isLoading: envoiRappel }] = useEnvoyerRappelFormateursMutation();

  const data = onglet === 'releves' ? dataReleves : dataSujets;
  const isLoading = onglet === 'releves' ? loadingReleves : loadingSujets;
  const isFetching = onglet === 'releves' ? fetchingReleves : fetchingSujets;
  const refetch = onglet === 'releves' ? refetchReleves : refetchSujets;

  // Modal + toast
  const [showConfirmRappel, setShowConfirmRappel] = useState<null | { count: number; label: string }>(null);
  const [rappelResult, setRappelResult] = useState<string | null>(null);

  // Filtre local : par statut d'onglet + recherche.
  // Pour les onglets Sujets, on privilégie les `groupes` du backend qui
  // regroupent (formateur × matière × niveau) — un formateur qui enseigne la
  // même matière dans plusieurs classes (tronc commun) compte pour 1 sujet.
  // Fix 2026-09-01 : pour LES DEUX onglets (relevés + sujets), privilégier les
  // `groupes` du backend qui déduplique par (formateur × matière × niveau).
  // Ex : "Rédaction Scientifique II" partagée L3CF+L3CGE avec même formateur
  // = 1 seule ligne agrégée (au lieu de 2). Le compteur "manquants" baisse.
  const lignesToutes = React.useMemo(() => {
    const groupes = (data as any)?.groupes as Array<any> | undefined;
    if (groupes && groupes.length > 0) {
      return groupes.map((g) => ({
        matiere_id: g.matieres_ids?.[0] || 0,
        code_matiere: (g.codes_matiere || []).join(', '),
        nom_matiere: g.nom_matiere,
        niveau: g.niveau,
        semestre_local: g.semestre_local,
        ue_nom: g.ue_nom,
        option_nom: (g.options || []).join(' · '),
        pole_nom: g.pole_nom,
        classe_code: (g.classes || []).join(' · '),
        classes_list: g.classes || [],
        enseignant: g.enseignant,
        statut: g.statut,
        nb_depose: g.nb_depose_total ?? g.nb_notes_total,
        nb_requis: g.nb_requis ?? g.nb_etudiants_attendus_total,
        nb_notes: g.nb_notes_total,
        nb_etudiants_attendus: g.nb_etudiants_attendus_total,
        derniere_date_depot: g.derniere_date_depot || g.date_derniere_saisie,
        date_derniere_saisie: g.date_derniere_saisie,
        depose_par_nom: g.depose_par_nom || g.saisi_par_nom,
        saisi_par_nom: g.saisi_par_nom,
        source: g.source,
        statut_depot: g.statut_depot,
        pas_de_devoir_autorise: g.pas_de_devoir_autorise,
      })) as any;
    }
    return (data?.lignes || []) as any;
  }, [onglet, data]);
  const lignesStatut = lignesToutes.filter((l: any) => l.statut === statutOnglet);
  const lignesFiltrees = lignesStatut.filter((l: any) => {
    if (!recherche.trim()) return true;
    const q = recherche.toLowerCase();
    return (
      (l.nom_matiere || '').toLowerCase().includes(q) ||
      (l.code_matiere || '').toLowerCase().includes(q) ||
      (l.enseignant?.nom_complet || '').toLowerCase().includes(q) ||
      (l.classe_code || '').toLowerCase().includes(q) ||
      (l.option_nom || '').toLowerCase().includes(q)
    );
  });

  // KPI : privilégier kpi_groupes pour TOUS les onglets (fix 2026-09-01).
  // Le compteur "manquants" reflète les groupes agrégés, pas les attributions
  // individuelles — 1 formateur × 1 matière homonyme = 1 relevé à faire.
  const kpi = (data as any)?.kpi_groupes || data?.kpi;
  const nbManquants = kpi?.manquant ?? 0;
  const nbPartiels = kpi?.partiel ?? 0;
  const nbDeposes = kpi?.depose ?? 0;

  // ─── Rappels ──────────────────────────────────────────────────────────────
  const typeRappel: 'devoir' | 'sujet_normale' | 'sujet_rattrapage' =
    onglet === 'releves' ? 'devoir'
    : onglet === 'sujets_normale' ? 'sujet_normale'
    : 'sujet_rattrapage';

  const handleRappelIndividuel = async (l: SuiviReleveLigne | SuiviSujetLigne) => {
    const uid = (l.enseignant as any).user_id as number | null | undefined;
    if (!uid) {
      alert("Ce formateur n'a pas de compte actif. Rappel impossible.");
      return;
    }
    try {
      const res = await envoyerRappel({
        type: typeRappel,
        cibles: [{
          user_id: uid,
          matiere_id: l.matiere_id,
          matiere_nom: l.nom_matiere,
          code_matiere: l.code_matiere,
        }],
      }).unwrap();
      setRappelResult(`Rappel envoyé à ${l.enseignant.nom_complet} (${res.nb_push_envoyees} appareil(s)).`);
      setTimeout(() => setRappelResult(null), 5000);
    } catch (err: any) {
      setRappelResult(`Erreur : ${err?.data?.error || 'envoi échoué'}`);
    }
  };

  const handleRappelMassif = async () => {
    setShowConfirmRappel(null);
    try {
      const res = await envoyerRappel({
        type: typeRappel,
        semestre, niveau: niveau || undefined, pole: pole || undefined,
      }).unwrap();
      setRappelResult(
        `Rappel envoyé à ${res.nb_formateurs_cibles} formateur(s) (${res.nb_push_envoyees} appareils). `
        + (res.nb_sans_compte_formateur > 0 ? `${res.nb_sans_compte_formateur} sans compte actif (non joignables).` : '')
      );
      setTimeout(() => setRappelResult(null), 8000);
    } catch (err: any) {
      setRappelResult(`Erreur : ${err?.data?.error || 'envoi échoué'}`);
    }
  };

  return (
    <div className="space-y-4">
      {/* Titre + description */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <ClipboardCheck size={26} className="text-blue-600" />
          Suivi Scolarité — Relevés & Sujets
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Tracer les relevés de devoirs déposés / manquants et les sujets d'examen des formateurs.
        </p>
      </div>

      {/* Onglets */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-6">
          {([
            { id: 'releves' as const, label: 'Relevés de devoirs', icon: <FileText size={15} /> },
            { id: 'sujets_normale' as const, label: 'Sujets — Ordinaire', icon: <ClipboardCheck size={15} /> },
            { id: 'sujets_rattrapage' as const, label: 'Sujets — Rattrapage', icon: <ClipboardCheck size={15} /> },
          ]).map((t) => (
            <button
              key={t.id}
              onClick={() => setOnglet(t.id)}
              className={`flex items-center gap-1.5 pb-3 text-sm font-medium border-b-2 transition-colors ${
                onglet === t.id
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
              }`}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Bandeau d'alerte si manquants */}
      {data && nbManquants > 0 && (
        <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 p-4 flex items-start gap-3">
          <AlertTriangle size={22} className="text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-red-800 dark:text-red-200">
              {nbManquants} {onglet === 'releves' ? 'relevé' : 'sujet'}{nbManquants > 1 ? 's' : ''} manquant{nbManquants > 1 ? 's' : ''}
              {nbPartiels > 0 && <span className="ml-1 font-normal">· {nbPartiels} partiel{nbPartiels > 1 ? 's' : ''}</span>}
            </div>
            <div className="text-sm text-red-700 dark:text-red-300 mt-0.5">
              Les formateurs concernés n'ont pas encore
              {onglet === 'releves' ? ' saisi leurs notes' : ' déposé leurs sujets'}
              {' '}pour ce semestre. Envoyez un rappel push aux formateurs délinquants.
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-1.5 flex-shrink-0">
            <button
              onClick={() => imprimerListeManquants(
                lignesToutes.filter((l: any) => l.statut === 'manquant'),
                {
                  onglet, semestre, annee: data?.annee_academique || '', niveau, pole,
                  institut: institutInfo,
                }
              )}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700 text-sm font-semibold rounded-md shadow-sm"
              title="Imprimer la liste des formateurs et matières concernés (déduplication par matière)"
            >
              <Printer size={14} /> Imprimer la liste
            </button>
            <button
              onClick={() => setShowConfirmRappel({
                count: nbManquants,
                label: onglet === 'releves' ? 'notes de devoir' : (onglet === 'sujets_normale' ? 'sujets ordinaire' : 'sujets rattrapage'),
              })}
              disabled={envoiRappel}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-md shadow-sm disabled:opacity-60"
            >
              {envoiRappel ? <Loader size={14} className="animate-spin" /> : <Bell size={14} />}
              Rappeler tous
            </button>
          </div>
        </div>
      )}

      {/* Toast résultat rappel */}
      {rappelResult && (
        <div className="rounded-lg border border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20 px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm text-emerald-800 dark:text-emerald-200">
            <CheckCircle2 size={14} /> {rappelResult}
          </div>
          <button onClick={() => setRappelResult(null)}><X size={14} className="text-emerald-700" /></button>
        </div>
      )}

      {/* Filtres + KPI */}
      {(
        <>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
            <div className="flex items-center gap-3 flex-wrap">
              {/* Semestre */}
              <div className="flex items-center gap-1.5 text-xs">
                <span className="text-gray-500 dark:text-gray-400">Semestre :</span>
                {[1, 2].map((s) => (
                  <button
                    key={s}
                    onClick={() => setSemestre(s as 1 | 2)}
                    className={`px-3 py-1.5 rounded font-medium transition-colors ${
                      semestre === s
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200'
                    }`}
                  >
                    S{s}
                  </button>
                ))}
              </div>

              {/* Niveau */}
              <div className="flex items-center gap-1.5 text-xs">
                <span className="text-gray-500 dark:text-gray-400">Niveau :</span>
                <select
                  value={niveau}
                  onChange={(e) => setNiveau(e.target.value)}
                  className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  {NIVEAUX.map((n) => (
                    <option key={n || 'tous'} value={n}>{n || 'Tous'}</option>
                  ))}
                </select>
              </div>

              {/* Pôle */}
              <div className="flex items-center gap-1.5 text-xs">
                <span className="text-gray-500 dark:text-gray-400">Pôle :</span>
                <select
                  value={pole}
                  onChange={(e) => setPole(e.target.value)}
                  className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="">Tous</option>
                  {poles.map((p) => (
                    <option key={p.id} value={p.nom}>{p.nom}</option>
                  ))}
                </select>
              </div>

              {/* Recherche */}
              <div className="relative flex-1 min-w-[200px]">
                <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={recherche}
                  onChange={(e) => setRecherche(e.target.value)}
                  placeholder="Rechercher matière, formateur, classe…"
                  className="w-full pl-7 pr-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>

              {/* Reload */}
              <button
                onClick={() => refetch()}
                disabled={isFetching}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-xs font-medium text-gray-700 dark:text-gray-300 rounded disabled:opacity-60"
              >
                {isFetching ? <Loader size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                Actualiser
              </button>
            </div>

            {/* KPI */}
            {data && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <KpiCard label="Total attributions" value={data.kpi.total} icon={<Users size={16} />} color="blue" />
                <KpiCard label="Déposés" value={data.kpi.depose} icon={<CheckCircle2 size={16} />} color="emerald" />
                <KpiCard label="Partiels" value={data.kpi.partiel} icon={<AlertTriangle size={16} />} color="amber" />
                <KpiCard label="Manquants" value={data.kpi.manquant} icon={<X size={16} />} color="red" />
              </div>
            )}
          </div>

          {/* Onglets par statut (Manquants / Partiels / Déposés) */}
          <div className="flex items-center gap-1 border-b border-gray-200 dark:border-gray-700">
            {([
              { id: 'manquant' as const, label: 'Manquants', count: nbManquants, color: 'red', bgActive: 'border-red-500 text-red-700 dark:text-red-300' },
              { id: 'partiel' as const, label: 'Partiels', count: nbPartiels, color: 'amber', bgActive: 'border-amber-500 text-amber-700 dark:text-amber-300' },
              { id: 'depose' as const, label: 'Déposés', count: nbDeposes, color: 'emerald', bgActive: 'border-emerald-500 text-emerald-700 dark:text-emerald-300' },
            ]).map((t) => (
              <button
                key={t.id}
                onClick={() => setStatutOnglet(t.id)}
                className={`inline-flex items-center gap-2 px-4 py-2 -mb-px border-b-2 text-sm font-medium transition-colors ${
                  statutOnglet === t.id
                    ? `${t.bgActive} bg-white dark:bg-gray-800`
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
              >
                {t.label}
                <span className={`inline-flex items-center justify-center min-w-[22px] h-5 text-[11px] font-bold rounded-full px-1.5 ${
                  statutOnglet === t.id
                    ? t.color === 'red' ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                    : t.color === 'amber' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                    : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-500'
                }`}>
                  {t.count}
                </span>
              </button>
            ))}
          </div>

          {/* Tableau */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            {isLoading ? (
              <div className="p-8 text-center text-gray-400 text-sm">
                <Loader size={16} className="inline-block animate-spin mr-2" /> Chargement…
              </div>
            ) : lignesFiltrees.length === 0 ? (
              <div className="p-8 text-center text-gray-400 text-sm">
                Aucune attribution ne correspond aux filtres.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 dark:bg-gray-700/50">
                    <tr>
                      {(onglet === 'releves'
                        ? ['Pôle', 'Matière', 'Classe', 'Formateur', 'Notes saisies', 'Source', 'Traçabilité', '']
                        : ['Pôle', 'Matière', 'Classe', 'Formateur', 'Dépôts', 'Traçabilité', '']
                      ).map((h, i) => (
                        <th key={h + i} className="px-3 py-2 text-left font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {lignesFiltrees.map((l: any, i: number) => (
                      onglet === 'releves'
                        ? <LigneRow
                            key={`${l.matiere_id}-${l.enseignant.id}-${i}`}
                            ligne={l as SuiviReleveLigne}
                            showRappel={statutOnglet !== 'depose'}
                            onRappel={() => handleRappelIndividuel(l)}
                            rappelPending={envoiRappel}
                          />
                        : <LigneSujetRow
                            key={`${l.matiere_id}-${l.enseignant.id}-${i}`}
                            ligne={l as SuiviSujetLigne}
                            showRappel={statutOnglet !== 'depose'}
                            onRappel={() => handleRappelIndividuel(l)}
                            rappelPending={envoiRappel}
                          />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Modal confirmation rappel massif */}
      {showConfirmRappel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setShowConfirmRappel(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Bell size={16} className="text-red-600" />
                Rappel massif aux formateurs
              </h3>
              <button onClick={() => setShowConfirmRappel(null)}><X size={16} /></button>
            </div>
            <div className="p-5 space-y-3 text-sm text-gray-800 dark:text-gray-200">
              <p>
                Vous êtes sur le point d'envoyer une notification push à
                <strong> tous les formateurs</strong> ayant au moins un manquement pour
                <strong> {showConfirmRappel.label}</strong> dans le périmètre :
              </p>
              <ul className="text-xs bg-gray-50 dark:bg-gray-900/40 rounded p-3 space-y-0.5 list-disc list-inside">
                <li>Semestre : <strong>S{semestre}</strong></li>
                <li>Niveau : <strong>{niveau || 'tous'}</strong></li>
                <li>Pôle : <strong>{pole || 'tous'}</strong></li>
                <li>Nombre estimé de manquants : <strong className="text-red-600">{showConfirmRappel.count}</strong></li>
              </ul>
              <p className="text-xs text-gray-500 italic">
                Un seul push par formateur, agrégeant toutes ses matières manquantes. Les formateurs sans compte actif ne recevront rien.
              </p>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button onClick={() => setShowConfirmRappel(null)} className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                  Annuler
                </button>
                <button
                  onClick={handleRappelMassif}
                  disabled={envoiRappel}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded disabled:opacity-60"
                >
                  {envoiRappel ? <Loader size={12} className="animate-spin" /> : <Send size={12} />}
                  Envoyer les rappels
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Ligne ────────────────────────────────────────────────────────────────────

const LigneRow: React.FC<{
  ligne: SuiviReleveLigne;
  showRappel: boolean;
  onRappel: () => void;
  rappelPending: boolean;
}> = ({ ligne, showRappel, onRappel, rappelPending }) => {
  const sourceBadge = (() => {
    if (ligne.source === 'formateur') return { bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-700 dark:text-blue-300', label: 'Formateur' };
    if (ligne.source === 'scolarite') return { bg: 'bg-purple-50 dark:bg-purple-900/20', text: 'text-purple-700 dark:text-purple-300', label: 'Scolarité' };
    if (ligne.source === 'mixte') return { bg: 'bg-indigo-50 dark:bg-indigo-900/20', text: 'text-indigo-700 dark:text-indigo-300', label: 'Mixte' };
    return null;
  })();

  const rowBg = ligne.statut === 'manquant' ? 'bg-red-50/40 dark:bg-red-900/10 hover:bg-red-50 dark:hover:bg-red-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700/30';

  const fmtDate = (iso: string | null) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  return (
    <tr className={rowBg}>
      <td className="px-3 py-2 whitespace-nowrap">
        <span className="inline-block px-2 py-0.5 rounded font-medium text-[10px] bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300">
          {ligne.pole_nom || '—'}
        </span>
      </td>
      <td className="px-3 py-2">
        <div className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-1.5 flex-wrap">
          {ligne.nom_matiere}
          {ligne.pas_de_devoir_autorise && (
            <span
              className="inline-block px-1.5 py-0.5 rounded font-semibold text-[9px] bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300"
              title={ligne.pas_de_devoir_motif ? `Motif : ${ligne.pas_de_devoir_motif}` : 'Le formateur a signalé qu\'il n\'y aurait pas de devoir. La note d\'examen sera auto-reportée.'}
            >
              AUTO-REPORT
            </span>
          )}
        </div>
        <div className="text-[10px] text-gray-500 dark:text-gray-400 font-mono">{ligne.code_matiere} · {ligne.ue_nom}</div>
      </td>
      <td className="px-3 py-2">
        <div className="font-medium text-gray-900 dark:text-gray-100">{ligne.classe_code || `${ligne.niveau} · ${ligne.option_nom}`}</div>
        <div className="text-[10px] text-gray-500 dark:text-gray-400">{ligne.niveau} · S{ligne.semestre_local}</div>
      </td>
      <td className="px-3 py-2">
        <div className="font-medium text-gray-900 dark:text-gray-100">{ligne.enseignant.nom_complet}</div>
        <div className="text-[10px] text-gray-500 dark:text-gray-400">
          {ligne.enseignant.telephone || '—'}
        </div>
      </td>
      <td className="px-3 py-2 text-center">
        <div className="font-bold text-gray-900 dark:text-gray-100">
          {ligne.nb_notes}<span className="text-gray-400">/{ligne.nb_etudiants_attendus}</span>
        </div>
        {ligne.nb_etudiants_attendus > 0 && (
          <div className="mt-1 h-1 w-16 mx-auto bg-gray-200 dark:bg-gray-600 rounded overflow-hidden">
            <div
              className={`h-1 ${ligne.statut === 'depose' ? 'bg-emerald-500' : ligne.statut === 'partiel' ? 'bg-amber-500' : 'bg-red-500'}`}
              style={{ width: `${Math.min(100, (ligne.nb_notes / ligne.nb_etudiants_attendus) * 100)}%` }}
            />
          </div>
        )}
      </td>
      <td className="px-3 py-2">
        {sourceBadge ? (
          <span className={`inline-block px-2 py-0.5 rounded font-medium text-[10px] ${sourceBadge.bg} ${sourceBadge.text}`}>
            {sourceBadge.label}
          </span>
        ) : (
          <span className="text-[10px] text-gray-400">—</span>
        )}
      </td>
      <td className="px-3 py-2 text-[10px] text-gray-600 dark:text-gray-400">
        {ligne.saisi_par_nom ? (
          <>
            <div className="flex items-center gap-1"><User size={10} /> {ligne.saisi_par_nom}</div>
            <div className="text-gray-400">Dernière saisie : {fmtDate(ligne.date_derniere_saisie)}</div>
          </>
        ) : (
          <span className="italic">Aucune saisie</span>
        )}
      </td>
      <td className="px-3 py-2 text-right">
        {showRappel && (
          <button
            onClick={onRappel}
            disabled={rappelPending}
            className="inline-flex items-center gap-1 px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-[10px] font-medium rounded disabled:opacity-60"
            title="Envoyer une notification push au formateur"
          >
            <Send size={10} /> Rappel
          </button>
        )}
      </td>
    </tr>
  );
};

// ─── Ligne SUJET ──────────────────────────────────────────────────────────────

const LigneSujetRow: React.FC<{
  ligne: SuiviSujetLigne & { classes_list?: string[] };
  showRappel: boolean;
  onRappel: () => void;
  rappelPending: boolean;
}> = ({ ligne, showRappel, onRappel, rappelPending }) => {
  const rowBg = ligne.statut === 'manquant' ? 'bg-red-50/40 dark:bg-red-900/10 hover:bg-red-50 dark:hover:bg-red-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700/30';
  const fmtDate = (iso: string | null) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return iso; }
  };
  const classesArr = (ligne.classes_list && ligne.classes_list.length > 0)
    ? ligne.classes_list
    : (ligne.classe_code ? [ligne.classe_code] : []);
  return (
    <tr className={rowBg}>
      <td className="px-3 py-2 whitespace-nowrap">
        <span className="inline-block px-2 py-0.5 rounded font-medium text-[10px] bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300">
          {ligne.pole_nom || '—'}
        </span>
      </td>
      <td className="px-3 py-2">
        <div className="font-semibold text-gray-900 dark:text-gray-100">{ligne.nom_matiere}</div>
        <div className="text-[10px] text-gray-500 dark:text-gray-400 font-mono">{ligne.code_matiere} · {ligne.ue_nom}</div>
      </td>
      <td className="px-3 py-2">
        {classesArr.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {classesArr.map((c) => (
              <span key={c} className="inline-block px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-[10px] font-mono">
                {c}
              </span>
            ))}
          </div>
        ) : (
          <div className="font-medium text-gray-900 dark:text-gray-100">{`${ligne.niveau} · ${ligne.option_nom}`}</div>
        )}
        <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">{ligne.niveau} · S{ligne.semestre_local}</div>
      </td>
      <td className="px-3 py-2">
        <div className="font-medium text-gray-900 dark:text-gray-100">{ligne.enseignant.nom_complet}</div>
        <div className="text-[10px] text-gray-500 dark:text-gray-400">
          {ligne.enseignant.telephone || '—'}
          {!ligne.enseignant.a_compte_actif && (
            <span className="ml-1 inline-block px-1 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-500 rounded text-[9px]">Pas de compte</span>
          )}
        </div>
      </td>
      <td className="px-3 py-2 text-center">
        <div className="font-bold text-gray-900 dark:text-gray-100">
          {ligne.nb_depose}<span className="text-gray-400">/{ligne.nb_requis}</span>
        </div>
        {ligne.nb_requis > 0 && (
          <div className="mt-1 h-1 w-16 mx-auto bg-gray-200 dark:bg-gray-600 rounded overflow-hidden">
            <div
              className={`h-1 ${ligne.statut === 'depose' ? 'bg-emerald-500' : ligne.statut === 'partiel' ? 'bg-amber-500' : 'bg-red-500'}`}
              style={{ width: `${Math.min(100, (ligne.nb_depose / ligne.nb_requis) * 100)}%` }}
            />
          </div>
        )}
      </td>
      <td className="px-3 py-2 text-[10px] text-gray-600 dark:text-gray-400">
        {ligne.depose_par_nom ? (
          <>
            <div className="flex items-center gap-1"><User size={10} /> {ligne.depose_par_nom}</div>
            <div className="text-gray-400">Dernier dépôt : {fmtDate(ligne.derniere_date_depot)}</div>
          </>
        ) : (
          <span className="italic">Aucun dépôt</span>
        )}
      </td>
      <td className="px-3 py-2 text-right">
        {showRappel && ligne.enseignant.a_compte_actif && (
          <button
            onClick={onRappel}
            disabled={rappelPending}
            className="inline-flex items-center gap-1 px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-[10px] font-medium rounded disabled:opacity-60"
            title="Envoyer une notification push au formateur"
          >
            <Send size={10} /> Rappel
          </button>
        )}
      </td>
    </tr>
  );
};

// ─── Petit KPI ────────────────────────────────────────────────────────────────

const KpiCard: React.FC<{ label: string; value: number; icon: React.ReactNode; color: 'blue' | 'emerald' | 'amber' | 'red' }> = ({ label, value, icon, color }) => {
  const colors: Record<string, string> = {
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800',
    emerald: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800',
    amber: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800',
    red: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800',
  };
  return (
    <div className={`rounded border p-2 flex items-center gap-2 ${colors[color]}`}>
      {icon}
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
        <div className="text-lg font-bold">{value}</div>
      </div>
    </div>
  );
};

export default SuiviScolarite;


// ═════════════════════════════════════════════════════════════════════════
// Impression de la liste des relevés/sujets manquants (fix 2026-09-01)
// Ouvre une fenêtre imprimable groupant les manquants par formateur, avec
// la liste des classes concernées. Utile pour transmettre à la direction
// ou pour affichage physique.
// ═════════════════════════════════════════════════════════════════════════
function imprimerListeManquants(
  lignes: any[],
  ctx: {
    onglet: string; semestre: number; annee: string; niveau: string; pole: string;
    institut?: {
      ville?: string; campus?: string;
      chef_scolarite?: string; chef_scolarite_titre?: string;
      telephone?: string; email?: string; adresse_rue?: string;
    };
  },
) {
  if (!lignes.length) { alert('Aucun manquant à imprimer.'); return; }
  const inst = ctx.institut || {};

  // Regrouper par formateur (id ou nom si pas d'id)
  const parFormateur = new Map<string, { nom: string; telephone: string; email: string; items: any[] }>();
  lignes.forEach(l => {
    const ens = l.enseignant || {};
    const key = String(ens.id || ens.nom_complet || '?');
    if (!parFormateur.has(key)) {
      parFormateur.set(key, {
        nom: ens.nom_complet || '(inconnu)',
        telephone: ens.telephone || '',
        email: ens.email || '',
        items: [],
      });
    }
    parFormateur.get(key)!.items.push(l);
  });
  const formateurs = Array.from(parFormateur.values()).sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));

  const dateStr = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
  const heureStr = new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  const typeLabel = ctx.onglet === 'releves' ? 'Relevés de devoirs'
    : ctx.onglet === 'sujets_normale' ? 'Sujets d\'examen (session ordinaire)'
    : 'Sujets d\'examen (rattrapage)';

  // En-tête officiel institut (fix 2026-09-01)
  const villeInst = inst.ville || 'Pointe-Noire';
  const campusLine = inst.campus || `Campus ${villeInst}`;
  const contactBits = [inst.telephone, inst.email, inst.adresse_rue].filter(Boolean).map(escapeHtml).join(' · ');
  const chefBloc = inst.chef_scolarite
    ? `<div class="chef">${escapeHtml(inst.chef_scolarite_titre || 'Chef de scolarité')} : <strong>${escapeHtml(inst.chef_scolarite)}</strong></div>`
    : '';

  const html = `<!doctype html><html lang="fr"><head><meta charset="utf-8">
<title>Manquants — ${typeLabel} — S${ctx.semestre}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, "Segoe UI", Roboto, sans-serif; color: #222; padding: 20px; margin: 0; font-size: 12px; }
  header.institut { text-align: center; padding-bottom: 12px; border-bottom: 2px solid #1e3a8a; margin-bottom: 14px; }
  header.institut .name { font-size: 16px; font-weight: 700; color: #1e3a8a; letter-spacing: 0.5px; text-transform: uppercase; }
  header.institut .campus { font-size: 12px; color: #555; margin-top: 2px; }
  header.institut .contact { font-size: 10px; color: #777; margin-top: 4px; }
  header.institut .chef { font-size: 10px; color: #333; margin-top: 6px; padding-top: 4px; border-top: 1px dashed #ccc; display: inline-block; padding: 4px 12px; }
  h1 { font-size: 16px; margin: 0 0 4px; text-align: center; }
  h2 { font-size: 13px; margin: 16px 0 6px; padding: 6px 10px; background: #fef2f2; border-left: 4px solid #dc2626; color: #991b1b; }
  .meta { font-size: 11px; color: #666; margin-bottom: 12px; text-align: center; }
  .synthese { display: flex; gap: 16px; padding: 10px 14px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 6px; margin-bottom: 20px; font-size: 12px; justify-content: center; }
  .synthese strong { color: #991b1b; font-size: 18px; }
  table { width: 100%; border-collapse: collapse; margin-top: 4px; }
  th, td { text-align: left; padding: 5px 8px; border: 1px solid #d4d4d8; font-size: 11px; vertical-align: top; }
  th { background: #f4f4f5; font-weight: 600; }
  .classes { color: #1e40af; font-weight: 600; font-family: ui-monospace, monospace; font-size: 10px; }
  .formateur-header { display: flex; justify-content: space-between; align-items: baseline; }
  .formateur-header .contact { font-size: 10px; color: #666; font-weight: normal; }
  .footer { margin-top: 30px; padding-top: 10px; border-top: 1px solid #d4d4d8; font-size: 10px; color: #666; text-align: center; }
  @media print { body { padding: 12px; } h2 { break-after: avoid; } table { break-inside: avoid; } header.institut { break-after: avoid; } }
</style></head><body>
<header class="institut">
  <div class="name">Institut International 2i</div>
  <div class="campus">${escapeHtml(campusLine)}</div>
  ${contactBits ? `<div class="contact">${contactBits}</div>` : ''}
  ${chefBloc}
</header>
<h1>${typeLabel} manquants</h1>
<div class="meta">
  Semestre <strong>S${ctx.semestre}</strong> · Année <strong>${ctx.annee || '—'}</strong>
  ${ctx.niveau ? ` · Niveau <strong>${ctx.niveau}</strong>` : ''}
  ${ctx.pole ? ` · Pôle <strong>${ctx.pole}</strong>` : ''}
  · Généré le ${dateStr} à ${heureStr}
</div>
<div class="synthese">
  <div><strong>${formateurs.length}</strong> formateur(s) concerné(s)</div>
  <div><strong>${lignes.length}</strong> matière(s) manquante(s)</div>
</div>
${formateurs.map(f => `
  <h2 class="formateur-header">
    <span>${escapeHtml(f.nom)}</span>
    <span class="contact">
      ${f.telephone ? escapeHtml(f.telephone) : ''}
      ${f.telephone && f.email ? ' · ' : ''}
      ${f.email ? escapeHtml(f.email) : ''}
    </span>
  </h2>
  <table>
    <thead>
      <tr>
        <th style="width:5%">#</th>
        <th style="width:50%">Matière</th>
        <th style="width:30%">Classe(s) concernée(s)</th>
        <th style="width:15%">Niveau</th>
      </tr>
    </thead>
    <tbody>
      ${f.items.map((it, idx) => `
        <tr>
          <td>${idx + 1}</td>
          <td>
            <strong>${escapeHtml(it.nom_matiere || '?')}</strong>
            ${it.ue_nom ? `<br><span style="color:#666;font-size:10px">${escapeHtml(it.ue_nom)}</span>` : ''}
          </td>
          <td class="classes">${escapeHtml((it.classes_list || [it.classe_code]).filter(Boolean).join(' · '))}</td>
          <td>${escapeHtml(it.niveau || '')}</td>
        </tr>
      `).join('')}
    </tbody>
  </table>
`).join('')}
<div class="footer">Institut International 2i — ${escapeHtml(campusLine)} — Suivi scolarité · ${dateStr} ${heureStr}</div>
<script>window.addEventListener('load', function(){ setTimeout(function(){ window.print(); }, 300); });</script>
</body></html>`;

  const w = window.open('', '_blank', 'width=1000,height=800');
  if (!w) { alert('Le popup a été bloqué. Autorisez les popups pour cette page.'); return; }
  w.document.write(html);
  w.document.close();
}

function escapeHtml(s: any): string {
  if (s === null || s === undefined) return '';
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] || c));
}
